import api from '../api';

/**
 * Envio de erros do frontend para o backend (`POST /api/client-errors`).
 *
 * Objetivo é ter o erro no log estruturado (pino) com `requestId`, sem depender
 * de Sentry. O reporte é **best-effort**:
 *
 * - nunca lança nem quebra o app (se o POST falhar, o erro segue só no console);
 * - deduplica por `mensagem + origem` (um render em loop não inunda o log);
 * - limita a 20 envios por sessão;
 * - trunca mensagem/stack para não enviar payload gigante.
 *
 * O endpoint só loga — não persiste nada (nada a podar no banco).
 */
const LIMITE_ENVIOS = 20;

const enviados = new Set();

const truncar = (valor, max) =>
  typeof valor === 'string' && valor.length > max ? `${valor.slice(0, max)}…` : valor;

/** Normaliza qualquer coisa lançada (Error, string, objeto) em mensagem/stack. */
const descreverErro = (erro) => {
  if (!erro) return { message: 'Erro desconhecido', stack: null };

  if (typeof erro === 'string') return { message: truncar(erro, 500), stack: null };

  const message = typeof erro.message === 'string' && erro.message ? erro.message : erro.name;

  return {
    message: truncar(message || 'Erro desconhecido', 500),
    stack: truncar(typeof erro.stack === 'string' ? erro.stack : null, 2000),
  };
};

const ambiente = () => {
  if (typeof window === 'undefined') return { url: null, userAgent: null };

  return {
    url: window.location?.pathname || null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };
};

/**
 * @param {unknown} erro erro capturado
 * @param {{ origem?: string, componentStack?: string, extra?: object }} contexto
 * @returns {boolean} `true` quando o envio foi disparado
 */
const reportarErro = (erro, contexto = {}) => {
  const { message, stack } = descreverErro(erro);
  const origem = contexto.origem || 'app';
  const chave = `${origem}|${message}`;

  try {
    console.error('[reportarErro]', erro, contexto);
  } catch {
    // console indisponível: segue o fluxo
  }

  if (enviados.has(chave) || enviados.size >= LIMITE_ENVIOS) return false;

  enviados.add(chave);

  const { url, userAgent } = ambiente();

  try {
    api
      .post('/client-errors', {
        message,
        stack,
        origem,
        url,
        userAgent,
        contexto: {
          componentStack: contexto.componentStack || null,
          ...(contexto.extra || {}),
        },
      })
      .catch(() => {});
  } catch {
    return false;
  }

  return true;
};

/**
 * Instala os listeners globais (`error` e `unhandledrejection`).
 * Devolve a função de limpeza (usada nos testes).
 */
const instalarCapturaGlobal = ({ alvo = typeof window !== 'undefined' ? window : null } = {}) => {
  if (!alvo || typeof alvo.addEventListener !== 'function' || alvo.__reportarErroInstalado) {
    return () => {};
  }

  const aoErro = (evento) => reportarErro(evento?.error || evento?.message, { origem: 'window' });
  const aoRejeitar = (evento) => reportarErro(evento?.reason, { origem: 'promise' });

  alvo.__reportarErroInstalado = true;
  alvo.addEventListener('error', aoErro);
  alvo.addEventListener('unhandledrejection', aoRejeitar);

  return () => {
    alvo.removeEventListener('error', aoErro);
    alvo.removeEventListener('unhandledrejection', aoRejeitar);
    alvo.__reportarErroInstalado = false;
  };
};

/** Só para testes: limpa o controle de deduplicação/limite. */
const resetarReportarErro = () => enviados.clear();

export { descreverErro, instalarCapturaGlobal, reportarErro, resetarReportarErro, LIMITE_ENVIOS };
